import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { catchError, debounceTime, from, map, Observable, of, switchMap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

type BackendFn = (value: any) => Promise<any> | Observable<any>;

interface VerifyOptions {
  message?: string;

  /**
   * Extract value before sending to backend
   * Example: (v) => v.id
   */
  mapValue?: (value: any) => any;

  /**
   * Transform backend response into boolean
   * Example: (res) => res.exists
   */
  mapResponse?: (response: any) => boolean;
}

export class FormValidators {

  static required(label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (control.value === null || control.value === undefined || control.value === '') {

        return {
          required: {
            message: `Please input ${label}`
          }
        };

      }

      return null;

    };

  }

  static requiredArray(label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (!Array.isArray(control.value) || control.value.length === 0) {

        return {
          requiredArray: {
            message: `Please select ${label}`
          }
        };

      }

      return null;

    };

  }

  static minLength(length: number, label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (!control.value) return null;

      if (control.value.length < length) {

        return {
          minlength: {
            message: `${label} must be at least ${length} characters`
          }
        };

      }

      return null;

    };

  }

  static maxLength(length: number, label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      const value = `${control.value}`;

      if (value.length > length) {

        return {
          maxlength: {
            message: `${label} must be at most ${length} characters`
          }
        };

      }

      return null;

    };

  }

  static minValue(min: number, label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      const numericValue = Number(control.value);

      if (!Number.isFinite(numericValue)) {
        return {
          minValue: {
            message: `${label} must be a valid number`
          }
        };
      }

      if (numericValue < min) {
        return {
          minValue: {
            message: `${label} must be at least ${min}`
          }
        };
      }

      return null;

    };

  }

  static maxValue(max: number, label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      const numericValue = Number(control.value);

      if (!Number.isFinite(numericValue)) {
        return {
          maxValue: {
            message: `${label} must be a valid number`
          }
        };
      }

      if (numericValue > max) {
        return {
          maxValue: {
            message: `${label} must be at most ${max}`
          }
        };
      }

      return null;

    };

  }

  static pattern(regex: RegExp, message: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (!control.value) return null;

      if (!regex.test(control.value)) {

        return {
          pattern: {
            message
          }
        };

      }

      return null;

    };

  }

  static compose(...validators: ValidatorFn[]): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      for (const validator of validators) {

        const result = validator(control);

        if (result) {
          return result;
        }

      }

      return null;

    };

  }

  static requiredIf(condition: (control: AbstractControl) => boolean, label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (!condition(control)) {
        return null;
      }

      if (control.value === null || control.value === undefined || control.value === '') {
        return {
          requiredIf: {
            message: `${label} is required`
          }
        };
      }

      return null;

    };

  }

  static name(label: string): ValidatorFn {

    return FormValidators.compose(

      FormValidators.required(label),

      FormValidators.pattern(
        /^[a-zA-ZÀ-ÿ' -]+$/,
        `${label} must contain only letters`
      )

    );

  }

  static email(label = 'Email'): ValidatorFn {

    return FormValidators.compose(

      FormValidators.required(label),

      FormValidators.pattern(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        `${label} is not valid`
      )

    );

  }

  static password(label = 'Password'): ValidatorFn {

    return FormValidators.compose(

      FormValidators.required(label),

      FormValidators.minLength(6, label)

    );

  }

  static matchingFields(first: string, second: string, message: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const firstValue = group.get(first)?.value;
      const secondValue = group.get(second)?.value;
      // Required validators own empty values; do not report two errors at once.
      if (!firstValue || !secondValue || firstValue === secondValue) return null;
      return { fieldsMismatch: { message } };
    };
  }

  static website(label = 'Website'): ValidatorFn {

    return this.compose(

      FormValidators.required(label),

      FormValidators.url(label)

    );

  }

  static url(label = 'URL'): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      const value = String(control.value).trim();
      if (!value || /\s/.test(value)) {
        return { url: { message: `${label} is not a valid URL` } };
      }

      try {
        const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const parsed = new URL(candidate);
        const supportedProtocol = parsed.protocol === 'http:' || parsed.protocol === 'https:';
        const validHostname = parsed.hostname === 'localhost' || parsed.hostname.includes('.');
        if (supportedProtocol && validHostname) return null;
      } catch {
        // Return the shared SmartForm validation shape below.
      }

      return { url: { message: `${label} is not a valid URL` } };
    };

  }

  static verifyFromBackend(
    backendFn: BackendFn,
    options?: VerifyOptions
  ): AsyncValidatorFn {

    return (control: AbstractControl) => {

      if (!control.value) return of(null);

      return of(control.value).pipe(

        debounceTime(500),

        switchMap(rawValue => {

          // ✅ 1. Extract value if needed
          const value = options?.mapValue
            ? options.mapValue(rawValue)
            : rawValue;

          const result = backendFn(value);

          return result instanceof Promise ? from(result) : result;

        }),

        map(response => {

          // ✅ 2. Convert response to boolean
          const isValid = options?.mapResponse
            ? options.mapResponse(response)
            : response; // assume boolean

          return isValid
            ? null
            : {
              backend: {
                message: options?.message || 'Invalid value'
              }
            };

        }),

        catchError(() => of(null))
      );

    };

  }

  static phone(label = 'Phone Number'): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (!control.value) return null;

      try {
        const phoneUtil = PhoneNumberUtil.getInstance();
        // Let google-libphonenumber auto-detect the country from the phone number
        const phoneNumber = phoneUtil.parse(control.value);

        if (!phoneUtil.isValidNumber(phoneNumber)) {
          return {
            phone: {
              message: `${label} is not a valid phone number`
            }
          };
        }

        return null;
      } catch (error) {
        return {
          phone: {
            message: `${label} is not a valid phone number`
          }
        };
      }

    };

  }

  static inArray(valuesOrGetter: any[] | (() => any[]), label: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {

      if (!control.value) return null;

      const values = typeof valuesOrGetter === 'function' ? valuesOrGetter() : valuesOrGetter;

      if (!Array.isArray(values) || values.length === 0) return null;

      if (!values.includes(control.value)) {

        return {
          invalidOption: {
            message: `${label} is not a valid option`
          }
        };

      }

      return null;

    };

  }

  static uuid(label: string = 'Field'): ValidatorFn {

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  return (control: AbstractControl): ValidationErrors | null => {

    if (!control.value) return null; // optional fields allowed

    if (!uuidRegex.test(control.value)) {
      return {
        uuid: {
          message: `${label} must be a valid UUID`
        }
      };
    }

    return null;
  };
}

}
